import React, { useState } from "react";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import Chip from "@mui/material/Chip";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import IconButton from "@mui/material/IconButton";
// import Button from "@mui/material/Button";
import { Global } from "@emotion/react";
import { styled } from "@mui/material/styles";
import CodeIcon from "@mui/icons-material/Code";
import SchoolIcon from "@mui/icons-material/School";
import WorkIcon from "@mui/icons-material/Work";
import BuildIcon from "@mui/icons-material/Build";
// import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility"; // For "View Details" button
import BusinessIcon from "@mui/icons-material/Business"; // For company
import GroupsIcon from "@mui/icons-material/Groups"; // For leadership
// import ScrollRevealSection from "../common/ScrollRevealSection";
import AnimatedSection from "../common/AnimatedSection";
import cytoLogo from "../../assets/logos/cyto.jpg";
import UniLogo from "../../assets/logos/R.png";
import {
  Avatar,
  Box,
  ListItem,
  ListItemText,
} from "@mui/material";

// Drawer styling
const drawerBleeding = 56;

const StyledBox = styled("div")(({ theme }) => ({
  backgroundColor: "rgba(15, 15, 26, 0.85)",
  backdropFilter: "blur(10px)",
  borderTop: "1px solid rgba(124, 58, 237, 0.2)",
}));

const Puller = styled("div")(({ theme }) => ({
  width: 30,
  height: 6,
  backgroundColor: "#7C3AED",
  borderRadius: 3,
  position: "absolute",
  top: 8,
  left: "calc(50% - 15px)",
}));

const educationData = [
  {
    degree: "Bachelor of Science in Computer Science",
    minor: "Cybersecurity",
    institution: "University at Buffalo, The State University of New York",
    period: "May 2026",
    logo: UniLogo,
    description:
      "Concentration in AI/ML; Cum Laude and Dean's List (2 times).",
    courses: [
      "Data Structures and Algorithms",
      "Distributed Systems",
      "Reinforcement Learning",
      "Machine Learning",
      "Operating Systems",
      "Networking Concepts",
      "Database Systems",
      "Software Engineering",
      "Computer Security",
      "CS Administration",
    ],
    gpa: "",
  },
];

// Content below is maintained from public/data/cv.md.
const skillsData = {
  "Programming Languages": [
    "Python",
    "Go",
    "C/C++",
    "Java",
    "TypeScript/JavaScript",
    "Rust",
    "Swift/SwiftUI",
    "Shell Scripting",
  ],
  Databases: [
    "PostgreSQL",
    "MySQL",
    "SQLite",
    "Redis",
    "Firebase/Firestore",
    "NoSQL",
    "Row-Level Locking",
    "Query Optimization",
    "Schema Design",
  ],
  "Backend & APIs": [
    "REST APIs",
    "FastAPI",
    "Microservices",
    "gRPC",
    "OAuth 2.0",
    "JWT",
    "Event-Driven Architecture",
    "WebSockets",
    "Flask",
    "Django",
    "Node.js",
  ],
  Frontend: ["React", "Next.js", "React Native", "Expo", "Material UI", "Figma"],
  "Cloud & Infrastructure": [
    "AWS",
    "GCP",
    "Docker",
    "Kubernetes",
    "CI/CD (GitHub, Jenkins)",
    "Distributed Systems",
    "Scalable Backend Infrastructure",
  ],
  "Network Security": [
    "pfSense",
    "Firewall Rule Design",
    "VPN Configuration",
    "IDS/IPS Concepts",
  ],
  "Identity & Access": [
    "Active Directory",
    "Group Policy",
    "Authentication Hardening",
  ],
  "Traffic Analysis": ["Wireshark", "Packet Capture", "Protocol Analysis"],
  "Application Security": ["SAST", "CSRF", "XSS", "Session Security", "Secure Coding Practices"],
  "Threat Intelligence": [
    "OpenCTI",
    "MISP",
    "OTX",
    "abuse.ch",
    "IOC Enrichment & Correlation",
  ],
  "Developer Tools": ["Git", "JIRA", "Agile/Scrum", "Claude Code", "Cursor"],
  "AI & ML": [
    "PyTorch",
    "TensorFlow",
    "NumPy",
    "Pandas",
    "Sklearn",
    "LangChain",
    "YOLOv8",
    "OpenCV",
    "LLM Infrastructure",
    "Semantic Embeddings",
    "Model Context Protocol (MCP)",
    "Agentic Workflows",
  ],
};

const toolsData = {
  "Development": ["Git", "JIRA", "Claude Code", "Cursor", "Figma"],
  "Cloud & Infrastructure": ["AWS", "GCP", "Docker", "Kubernetes", "CI/CD (GitHub, Jenkins)"],
  "Security": ["pfSense", "Wireshark", "SAST", "OpenCTI", "MISP", "OTX"],
  "Data & AI": ["PostgreSQL", "MySQL", "SQLite", "PyTorch", "TensorFlow", "LangChain"],
  "Backend": ["FastAPI", "gRPC", "OAuth 2.0", "JWT", "WebSockets", "Node.js"],
};

const experienceData = [
  {
    title: "Tech Consultant",
    company: "UB Information & Technology Department",
    period: "Aug 2023 - Jun 2026",
    logo: UniLogo,
    description:
      "Provided hands-on Tier 2 support across Windows, macOS, and Linux for faculty, students, and researchers.",
    detailedDescription:
      "Resolved complex technical, authentication, and security issues for the University at Buffalo community.",
    responsibilities: [
      "Provided hands-on Tier 2 support to 2,000+ users, including faculty, students, and researchers, resolving complex technical, authentication, and security issues across Windows, macOS, and Linux systems.",
      "Resolved issues related to operating systems, networking, and enterprise software.",
      "Used enterprise tools to image and configure Windows and macOS desktops and laptops and managed client issues with ticketing software.",
    ],
    technologies: ["Windows", "macOS", "Linux", "Networking", "Enterprise Software", "Ticketing Systems"],
    achievements: ["Supported 2,000+ users"],
  },
  {
    title: "Teaching Assistant - CSE 365: Intro to Computer Security",
    company: "University at Buffalo Department of Computer Science",
    period: "Aug 2025 - Jun 2026",
    logo: UniLogo,
    description:
      "Mentored students through hands-on operating systems, networking, and security labs.",
    detailedDescription:
      "Maintained Linux-based teaching infrastructure and assisted with VMs, containerized labs, and backend APIs.",
    responsibilities: [
      "Mentored 90+ students through hands-on labs covering OS internals, memory corruption, TCP/IP networking, and exploitation.",
      "Led practical sessions analyzing Shellshock, buffer overflows, TCP attacks, and packet-level traffic.",
      "Maintained Linux-based teaching infrastructure and assisted in diagnosing issues across VMs, containerized labs, and backend APIs.",
    ],
    technologies: ["Linux", "TCP/IP", "VMs", "Containers", "Backend APIs", "Cybersecurity"],
    achievements: ["Mentored 90+ students"],
  },
  {
    title: "Software Engineer",
    company: "Cytocybernetics",
    period: "Jan 2024 - May 2025",
    logo: cytoLogo,
    description:
      "Designed electrophysiology data pipelines and real-time analysis systems processing over one million streaming datasets.",
    detailedDescription:
      "Built performance-focused software and modular hardware-configuration tooling in Python, C++, and GTK.",
    responsibilities: [
      "Designed and shipped a streamlined electrophysiology data-pipeline with a vectorized Python processing layer that boosted throughput 60% and enabled 10× larger datasets without scaling infrastructure.",
      "Contributed to a high-performance C++ hardware validation suite, leveraging multithreaded I/O and low-level device APIs to improve data throughput and reduce testing overhead by 65%.",
      "Engineered a multi-threaded C++/OpenMP engine for real-time analysis of over one million streaming electrophysiology datasets, achieving near-zero latency through memory optimization and parallel execution.",
      "Delivered a modular C/GTK platform with GObject plug-ins and dynamic hardware modules that powered real-time electrophysiology workflows used by researchers and pharmaceutical developers.",
    ],
    technologies: ["Python", "Pandas", "NumPy", "C++", "OpenMP", "C/GTK", "GObject"],
    achievements: ["60% throughput increase", "1M+ streaming datasets", "65% testing overhead reduction"],
  },
];

const leadershipData = [
  {
    title: "Founding President",
    organization: "Nepali Student Association",
    period: "University at Buffalo",
    logo: null,
    description: "Leadership activity listed in the CV.",
    responsibilities: ["Founding President — Nepali Student Association."],
  },
  {
    title: "Professional Development Chair",
    organization: "Theta Tau Coed Professional Fraternity",
    period: "University at Buffalo",
    logo: null,
    description: "Leadership activity listed in the CV.",
    responsibilities: ["Professional Development Chair — Theta Tau Coed Professional Fraternity."],
  },
  {
    title: "Red Team",
    organization: "UB Lockdown NetDef",
    period: "University at Buffalo",
    logo: null,
    description: "Activity listed in the CV.",
    responsibilities: ["Red Team — UB Lockdown NetDef."],
  },
];

const AboutSection = () => {
  const [openDrawers, setOpenDrawers] = useState({
    skills: false,
    experience: false,
    education: false,
    tools: false,
  });

  // Existing state for experience modal
  const [openModal, setOpenModal] = useState(false);
  const [selectedExperience, setSelectedExperience] = useState(null);

  // Add these new states for education modal
  const [openEducationModal, setOpenEducationModal] = useState(false);
  const [selectedEducation, setSelectedEducation] = useState(null);

  // Add new state for leadership modal
  const [openLeadershipModal, setOpenLeadershipModal] = useState(false);
  const [selectedLeadership, setSelectedLeadership] = useState(null);

  // Existing handlers
  const toggleDrawer = (drawer, open) => () => {
    setOpenDrawers({ ...openDrawers, [drawer]: open });
  };

  const handleOpenModal = (experience) => {
    setSelectedExperience(experience);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  // Add new handlers for education modal
  const handleOpenEducationModal = (education) => {
    setSelectedEducation(education);
    setOpenEducationModal(true);
  };

  const handleCloseEducationModal = () => {
    setOpenEducationModal(false);
  };

  // Add new handlers for leadership modal
  const handleOpenLeadershipModal = (leadership) => {
    setSelectedLeadership(leadership);
    setOpenLeadershipModal(true);
  };

  const handleCloseLeadershipModal = () => {
    setOpenLeadershipModal(false);
  };

  // Add skill icons mapping - complete version
  const skillIcons = {
    "Python": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg",
    "C": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/c/c-original.svg",
    "C++": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/cplusplus/cplusplus-original.svg",
    "Go": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/go/go-original.svg",
    "Java": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg",
    "JavaScript": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg",
    "React": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg",
    "HTML": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/html5/html5-original.svg",
    "PHP": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/php/php-original.svg",
    "MySQL": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg",
    "Node.js": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg",
    "Next.js": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nextjs/nextjs-original.svg",
    "Flask": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/flask/flask-original.svg",
    "Material-UI": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/materialui/materialui-original.svg",
    "Linux": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/linux/linux-original.svg",
    "Linux (Kali, RedHat, Fedora)": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/linux/linux-original.svg",
    "MacOS": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/apple/apple-original.svg",
    "Windows": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/windows8/windows8-original.svg",
    "Windows (Server and Client)": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/windows8/windows8-original.svg",
    "Ubuntu": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/ubuntu/ubuntu-plain.svg",
    "Ubuntu (Server and Client)": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/ubuntu/ubuntu-plain.svg",
    "VS Code": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/vscode/vscode-original.svg",
    "GitHub": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/github/github-original.svg",
    "Git": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg",
    "Git & GitHub": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg",
    "Docker": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg",
    "PostgreSQL": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postgresql/postgresql-original.svg",
    "Firebase": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/firebase/firebase-plain.svg",
    "Jupyter Notebook": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/jupyter/jupyter-original.svg",
    "Figma": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/figma/figma-original.svg",
    "IntelliJ": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/intellij/intellij-original.svg",
    "PyCharm": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/pycharm/pycharm-original.svg",
    "Pandas": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/pandas/pandas-original.svg",
    "NumPy": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/numpy/numpy-original.svg",
    "OpenCV": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/opencv/opencv-original.svg",
    "PyTorch": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/pytorch/pytorch-original.svg",
    "AWS": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/amazonwebservices/amazonwebservices-original.svg",
    "Google Cloud": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/googlecloud/googlecloud-original.svg",
    "Google Cloud Platform": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/googlecloud/googlecloud-original.svg",
    "WordPress": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/wordpress/wordpress-original.svg",
    "CSS": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/css3/css3-original.svg",
    "Kubernetes": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/kubernetes/kubernetes-plain.svg",
    "Android Studio": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/android/android-original.svg",
    "Shell Scripting": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/bash/bash-original.svg",
    "LaTeX": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/latex/latex-original.svg",
    "C/C++": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/cplusplus/cplusplus-original.svg",
    "Emacs": "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/emacs/emacs-original.svg",
    // Security tools with available icons
    "Wireshark": "https://upload.wikimedia.org/wikipedia/commons/c/c6/Wireshark_icon_new.png",
    "Nmap": "https://nmap.org/images/nmap-logo-256x256.png",
    "Kali Linux": "https://www.kali.org/images/kali-logo.svg",
    // For tools without standard icons, we'll return null and handle it in the function
  };

  // Helper function to get skill icon
  const getSkillIcon = (skill) => {
    const iconUrl = skillIcons[skill];
    if (iconUrl && iconUrl.startsWith('http')) {
      return (
        <Avatar
          src={iconUrl}
          alt={skill}
          sx={{
            width: 20,
            height: 20,
            mr: 1,
            backgroundColor: "transparent",
            "& .MuiAvatar-img": {
              objectFit: "contain",
            },
          }}
          onError={(e) => {
            // If image fails to load, hide the avatar
            e.target.style.display = 'none';
          }}
        />
      );
    }
    // Return null for skills without icons - no fallback dot or emoji
    return null;
  };

  // Drawer category components
  const drawerCategories = [
    {
      id: "skills",
      title: "Skills",
      icon: <CodeIcon sx={{ color: "#7C3AED", fontSize: "1.8rem" }} />,
      content: (
        <Box sx={{ py: 2 }}>
          {Object.entries(skillsData).map(([category, skills]) => (
            <Box key={category} sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{
                  color: "#7C3AED",
                  mb: 2,
                  borderBottom: "1px solid rgba(124, 58, 237, 0.3)",
                  pb: 1,
                }}
              >
                {category}
              </Typography>

              <Grid container spacing={2}>
                {skills.map((skill, index) => (
                  <Grid item xs={6} sm={4} md={3} key={index}>
                    <Chip
                      avatar={getSkillIcon(skill)}
                      label={skill}
                      sx={{
                        backgroundColor: "rgba(124, 58, 237, 0.08)",
                        color: "rgba(255,255,255,0.9)",
                        border: "1px solid rgba(124, 58, 237, 0.3)",
                        borderRadius: "16px",
                        p: 0.5,
                        width: "100%",
                        "& .MuiChip-label": {
                          px: 1,
                          py: 0.5,
                          whiteSpace: "normal",
                          textAlign: "center",
                        },
                        "& .MuiChip-avatar": {
                          ml: 1,
                        },
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        </Box>
      ),
    },
    {
      id: "experience",
      title: "Experience",
      icon: <WorkIcon sx={{ color: "#7C3AED", fontSize: "1.8rem" }} />,
      content: (
        <Grid container spacing={2} sx={{ py: 2 }}>
          {experienceData.map((exp, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <Card
                sx={{
                  backgroundColor: "rgba(124, 58, 237, 0.05)",
                  border: "1px solid rgba(124, 58, 237, 0.2)",
                  borderRadius: 2,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      paddingRight: "40px",
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", mb: 1.5 }}
                    >
                      {exp.logo ? (
                        <Avatar
                          src={exp.logo}
                          alt={`${exp.company} logo`}
                          sx={{
                            width: 40,
                            height: 40,
                            mr: 2,
                            backgroundColor: "rgba(255, 255, 255, 1)",
                            "& .MuiAvatar-img": {
                              objectFit: "contain",
                              padding: "6px",
                            },
                          }}
                          variant="rounded"
                        />
                      ) : (
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            mr: 2,
                            backgroundColor: "rgba(124, 58, 237, 0.1)",
                            border: "1px solid rgba(124, 58, 237, 0.3)",
                          }}
                        >
                          <BusinessIcon
                            sx={{ color: "#7C3AED", fontSize: "1.2rem" }}
                          />
                        </Avatar>
                      )}
                      <Typography
                        variant="h6"
                        color="#7C3AED"
                        gutterBottom
                        sx={{ mb: 0 }}
                      >
                        {exp.title}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        color="rgba(255,255,255,0.9)"
                      >
                        {exp.company}
                      </Typography>
                      <Typography variant="body2" color="rgba(255,255,255,0.9)">
                        {exp.period}
                      </Typography>
                    </Box>
                    <Divider
                      sx={{ my: 1.5, bgcolor: "rgba(124, 58, 237, 0.1)" }}
                    />
                    <Typography variant="body2" color="rgba(255,255,255,0.9)">
                      {exp.description}
                    </Typography>

                    {/* Add technology chips with icons */}
                    <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {exp.technologies.slice(0, 3).map((tech, techIndex) => (
                        <Chip
                          key={techIndex}
                          avatar={getSkillIcon(tech)}
                          label={tech}
                          size="small"
                          sx={{
                            backgroundColor: "rgba(124, 58, 237, 0.05)",
                            color: "rgba(255,255,255,0.8)",
                            border: "1px solid rgba(124, 58, 237, 0.2)",
                            fontSize: "0.7rem",
                            height: "24px",
                            "& .MuiChip-avatar": {
                              width: 16,
                              height: 16,
                            },
                          }}
                        />
                      ))}
                      {exp.technologies.length > 3 && (
                        <Chip
                          label={`+${exp.technologies.length - 3} more`}
                          size="small"
                          sx={{
                            backgroundColor: "rgba(124, 58, 237, 0.05)",
                            color: "rgba(255,255,255,0.6)",
                            border: "1px solid rgba(124, 58, 237, 0.2)",
                            fontSize: "0.7rem",
                            height: "24px",
                          }}
                        />
                      )}
                    </Box>
                  </Box>

                  <IconButton
                    onClick={() => handleOpenModal(exp)}
                    sx={{
                      color: "rgba(124,58,237,0.7)",
                      position: "absolute",
                      top: 16,
                      right: 16,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        color: "#7C3AED",
                        transform: "scale(1.1)",
                      },
                    }}
                  >
                    <VisibilityIcon />
                  </IconButton>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ),
    },
    {
      id: "education",
      title: "Education",
      icon: <SchoolIcon sx={{ color: "#7C3AED", fontSize: "1.8rem" }} />,
      content: (
        <Grid container spacing={2} sx={{ py: 2 }}>
          {educationData.map((edu, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <Card
                sx={{
                  backgroundColor: "rgba(124, 58, 237, 0.05)",
                  border: "1px solid rgba(124, 58, 237, 0.2)",
                  borderRadius: 2,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      paddingRight: "40px", // Make room for the eye icon
                    }}
                  >
                    {/* Institution logo/avatar */}
                    <Box
                      sx={{ display: "flex", alignItems: "center", mb: 1.5 }}
                    >
                      {edu.logo ? (
                        <Avatar
                          src={edu.logo}
                          alt={`${edu.institution} logo`}
                          sx={{
                            width: 40,
                            height: 40,
                            mr: 2,
                            // border: "1px solid rgba(124, 58, 237, 0.3)",
                            backgroundColor: "rgba(255, 255, 255, 1)",
                            "& .MuiAvatar-img": {
                              objectFit: "contain", // This will prevent cropping
                              padding: "4px", // Add some padding inside the avatar
                            },
                          }}
                          variant="rounded"
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "rgba(124, 58, 237, 0.1)",
                            border: "1px solid rgba(124, 58, 237, 0.3)",
                            mr: 2,
                          }}
                        >
                          <SchoolIcon
                            sx={{ color: "#7C3AED", fontSize: "1.2rem" }}
                          />
                        </Box>
                      )}
                      <Typography
                        variant="h6"
                        color="#7C3AED"
                        gutterBottom
                        sx={{ mb: 0 }}
                      >
                        {edu.degree || edu.certificate}
                      </Typography>
                    </Box>

                    {/* Add minor if it exists */}
                    {edu.minor && (
                      <Typography
                        variant="body1"
                        color="rgba(255,255,255,0.9)"
                        sx={{
                          mb: 1,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Box component="span" sx={{ color: "#7C3AED", mr: 1 }}>
                          Minor:
                        </Box>{" "}
                        {edu.minor}
                      </Typography>
                    )}

                    {/* Institution and period */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        color="rgba(255,255,255,0.9)"
                      >
                        {edu.institution}
                      </Typography>
                      <Typography variant="body2" color="rgba(255,255,255,0.9)">
                        {edu.period}
                      </Typography>
                    </Box>

                    <Divider
                      sx={{ my: 1.5, bgcolor: "rgba(124, 58, 237, 0.1)" }}
                    />

                    <Typography variant="body2" color="rgba(255,255,255,0.9)">
                      {edu.description}
                    </Typography>
                  </Box>

                  {/* Add eye button for education cards */}
                  <IconButton
                    onClick={() => handleOpenEducationModal(edu)}
                    sx={{
                      color: "rgba(124,58,237,0.7)",
                      position: "absolute",
                      top: 16,
                      right: 16,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        color: "#7C3AED",
                        transform: "scale(1.1)",
                      },
                    }}
                    aria-label="view courses"
                  >
                    <VisibilityIcon />
                  </IconButton>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ),
    },
    {
      id: "tools",
      title: "Tools",
      icon: <BuildIcon sx={{ color: "#7C3AED", fontSize: "1.8rem" }} />,
      content: (
        <Box sx={{ py: 2 }}>
          {Object.entries(toolsData).map(([category, tools]) => (
            <Box key={category} sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{
                  color: "#7C3AED",
                  mb: 2,
                  borderBottom: "1px solid rgba(124, 58, 237, 0.3)",
                  pb: 1,
                }}
              >
                {category}
              </Typography>

              <Grid container spacing={2}>
                {tools.map((tool, index) => (
                  <Grid item xs={6} sm={4} md={3} key={index}>
                    <Chip
                      avatar={getSkillIcon(tool)}
                      label={tool}
                      sx={{
                        backgroundColor: "rgba(124, 58, 237, 0.08)",
                        color: "rgba(255,255,255,0.9)",
                        border: "1px solid rgba(124, 58, 237, 0.3)",
                        borderRadius: "16px",
                        p: 0.5,
                        width: "100%",
                        "& .MuiChip-label": {
                          px: 1,
                          py: 0.5,
                          whiteSpace: "normal",
                          textAlign: "center",
                        },
                        "& .MuiChip-avatar": {
                          ml: 1,
                        },
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        </Box>
      ),
    },
    {
      id: "leadership",
      title: "Leadership",
      icon: <GroupsIcon sx={{ color: "#7C3AED", fontSize: "1.8rem" }} />,
      content: (
        <Grid container spacing={2} sx={{ py: 2 }}>
          {leadershipData.map((leadership, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <Card
                sx={{
                  backgroundColor: "rgba(124, 58, 237, 0.05)",
                  border: "1px solid rgba(124, 58, 237, 0.2)",
                  borderRadius: 2,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      paddingRight: "40px", // Make room for the eye icon
                    }}
                  >
                    {/* Organization logo/avatar */}
                    <Box
                      sx={{ display: "flex", alignItems: "center", mb: 1.5 }}
                    >
                      {leadership.logo ? (
                        <Avatar
                          src={leadership.logo}
                          alt={`${leadership.organization} logo`}
                          sx={{
                            width: 40,
                            height: 40,
                            mr: 2,
                            backgroundColor: "rgba(255, 255, 255, 0.05)",
                            "& .MuiAvatar-img": {
                              objectFit: "contain",
                              padding: "6px",
                            },
                          }}
                          variant="rounded"
                        />
                      ) : (
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            mr: 2,
                            backgroundColor: "rgba(124, 58, 237, 0.1)",
                            border: "1px solid rgba(124, 58, 237, 0.3)",
                          }}
                        >
                          <GroupsIcon
                            sx={{ color: "#7C3AED", fontSize: "1.2rem" }}
                          />
                        </Avatar>
                      )}
                      <Typography
                        variant="h6"
                        color="#7C3AED"
                        gutterBottom
                        sx={{ mb: 0 }}
                      >
                        {leadership.title}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        color="rgba(255,255,255,0.9)"
                      >
                        {leadership.organization}
                      </Typography>
                      <Typography variant="body2" color="rgba(255,255,255,0.9)">
                        {leadership.period}
                      </Typography>
                    </Box>
                    <Divider
                      sx={{ my: 1.5, bgcolor: "rgba(124, 58, 237, 0.1)" }}
                    />
                    <Typography variant="body2" color="rgba(255,255,255,0.9)">
                      {leadership.description}
                    </Typography>
                  </Box>

                  {/* Add button to see details */}
                  <IconButton
                    onClick={() => handleOpenLeadershipModal(leadership)}
                    sx={{
                      color: "rgba(124,58,237,0.7)",
                      position: "absolute",
                      top: 16,
                      right: 16,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        color: "#7C3AED",
                        transform: "scale(1.1)",
                      },
                    }}
                  >
                    <VisibilityIcon />
                  </IconButton>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ),
    },
  ];

  return (
    <AnimatedSection id="about">
      <Container maxWidth="lg" sx={{ textAlign: "center" }}>
        <Box sx={{ pt: 10, pb: 8 }}>
          <Typography
            variant="h3"
            component="h2"
            sx={{
              mb: 2,
              pb: 2,
              textAlign: "center",
              background: "linear-gradient(90deg, #FFFFFF 0%, #A78BFA 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            About Me
          </Typography>

          <Typography
            variant="body1"
            paragraph
            sx={{
              color: "rgba(255,255,255,0.7)",
              textAlign: "center",
              maxWidth: "800px",
              mx: "auto",
              mb: 6,
            }}
          >
            I’m a computer science graduate from the University at Buffalo
            specializing in backend systems, AI/ML, systems, and full-stack
            engineering. My professional experience spans enterprise IT support,
            security teaching, and software engineering. My technical projects
            span payments, LLM tooling, robotics and inspection, and cloud
            optimization. I focus on high-concurrency solutions with measurable
            technical impact.
          </Typography>

          {/* Drawer Category Cards */}
          <Grid
            container
            spacing={3}
            sx={{
              mb: 4,
              justifyContent: "center",
              mx: "auto",
              maxWidth: "1000px",
            }}
          >
            {drawerCategories.map((category) => (
              <Grid item xs={6} sm={6} md={3} key={category.id}>
                <Paper
                  elevation={0}
                  onClick={toggleDrawer(category.id, true)}
                  sx={{
                    p: 2,
                    backgroundColor: "rgba(124, 58, 237, 0.05)",
                    border: "1px solid rgba(124, 58, 237, 0.2)",
                    borderRadius: 2,
                    transition: "transform 0.3s, background-color 0.3s",
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "80px",
                    width: "100%",
                    padding: "5px",
                    "&:hover": {
                      transform: "translateY(-5px)",
                      backgroundColor: "rgba(124, 58, 237, 0.08)",
                    },
                  }}
                >
                  {category.icon}
                  <Typography
                    variant="h6"
                    sx={{
                      ml: 1.5,
                      fontSize: { xs: "1rem", sm: "1.25rem" },
                    }}
                    color="rgba(255,255,255,0.7)"
                  >
                    {category.title}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Swipeable Drawers */}
          <Global
            styles={{
              ".MuiDrawer-root > .MuiPaper-root": {
                height: `calc(60% - ${drawerBleeding}px)`,
                overflow: "visible",
                borderTopLeftRadius: "16px",
                borderTopRightRadius: "16px",
              },
              // Fix for drawer always being visible
              ".MuiDrawer-root .MuiBackdrop-root": {
                backgroundColor: "rgba(0, 0, 0, 0.5)",
              },
            }}
          />

          {drawerCategories.map((category) => (
            <SwipeableDrawer
              key={category.id}
              anchor="bottom"
              open={openDrawers[category.id]}
              onClose={toggleDrawer(category.id, false)}
              onOpen={toggleDrawer(category.id, true)}
              swipeAreaWidth={0} // Disable swipe to open
              disableSwipeToOpen={true}
              ModalProps={{
                keepMounted: false, // Change this to fix always visible issue
              }}
              sx={{
                visibility: openDrawers[category.id] ? "visible" : "hidden",
                "& .MuiPaper-root": {
                  visibility: "visible",
                },
              }}
            >
              <StyledBox
                sx={{
                  position: "absolute",
                  top: -drawerBleeding,
                  borderTopLeftRadius: 8,
                  borderTopRightRadius: 8,
                  visibility: "visible",
                  right: 0,
                  left: 0,
                  backgroundColor: "rgba(15, 15, 26, 0.9)",
                  backdropFilter: "blur(10px)",
                  borderTop: "1px solid rgba(124, 58, 237, 0.2)",
                }}
              >
                <Puller />
                <Typography
                  sx={{
                    p: 2,
                    color: "#7C3AED",
                    textAlign: "center",
                    fontWeight: "medium",
                  }}
                >
                  {category.title}
                </Typography>
              </StyledBox>
              <StyledBox
                sx={{
                  px: 3,
                  pb: 3,
                  pt: 1,
                  height: "100%",
                  overflow: "auto",
                  "&::-webkit-scrollbar": {
                    width: "8px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "rgba(0,0,0,0.1)",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "rgba(124,58,237,0.3)",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    background: "rgba(124,58,237,0.5)",
                  },
                }}
              >
                {category.content}
              </StyledBox>
            </SwipeableDrawer>
          ))}

          {/* Modal for Experience Details */}
          <Modal
            open={openModal}
            onClose={handleCloseModal}
            closeAfterTransition
            aria-labelledby="experience-details-modal"
            aria-describedby="detailed-experience-information"
          >
            <Fade in={openModal}>
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: { xs: "90%", sm: "80%", md: "600px" },
                  bgcolor: "rgba(15, 15, 26, 0.95)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(124, 58, 237, 0.3)",
                  borderRadius: 2,
                  boxShadow: 24,
                  p: 4,
                  maxHeight: "80vh",
                  overflow: "auto",
                  "&::-webkit-scrollbar": {
                    width: "8px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "rgba(0,0,0,0.1)",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "rgba(124,58,237,0.3)",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    background: "rgba(124,58,237,0.5)",
                  },
                }}
              >
                {selectedExperience && (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="h4"
                        component="h2"
                        sx={{ color: "#7C3AED" }}
                      >
                        {selectedExperience.title}
                      </Typography>
                      <IconButton
                        onClick={handleCloseModal}
                        sx={{
                          color: "rgba(255,255,255,0.7)",
                          "&:hover": { color: "#7C3AED" },
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 3,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ color: "rgba(255,255,255,0.9)" }}
                      >
                        {selectedExperience.company}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "rgba(255,255,255,0.7)" }}
                      >
                        {selectedExperience.period}
                      </Typography>
                    </Box>

                    {/* Add this after the company name in the modal */}
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      {selectedExperience.logo ? (
                        <Avatar
                          src={selectedExperience.logo}
                          alt={`${selectedExperience.company} logo`}
                          sx={{
                            width: 60,
                            height: 60,
                            mr: 2,
                            border: "1px solid rgba(124, 58, 237, 0.3)",
                            backgroundColor: "rgba(255, 255, 255, 0.05)",
                            "& .MuiAvatar-img": {
                              objectFit: "contain", // This will prevent cropping
                              padding: "6px", // Add some padding inside the avatar
                            },
                          }}
                          variant="rounded" // Rounded corners look more professional for logos
                        />
                      ) : (
                        <Avatar
                          sx={{
                            width: 60,
                            height: 60,
                            mr: 2,
                            backgroundColor: "rgba(124, 58, 237, 0.1)",
                            border: "1px solid rgba(124, 58, 237, 0.3)",
                          }}
                          variant="rounded"
                        >
                          <BusinessIcon
                            sx={{ color: "#7C3AED", fontSize: "2rem" }}
                          />
                        </Avatar>
                      )}
                      <Typography
                        variant="h5"
                        sx={{ color: "rgba(255,255,255,0.9)" }}
                      >
                        {selectedExperience.company}
                      </Typography>
                    </Box>

                    <Typography
                      variant="body1"
                      sx={{ color: "rgba(255,255,255,0.9)", mb: 3 }}
                    >
                      {selectedExperience.detailedDescription}
                    </Typography>

                    <Typography
                      variant="h6"
                      sx={{ color: "#7C3AED", mb: 2 }}
                    >
                      Key Responsibilities:
                    </Typography>

                    <List>
                      {selectedExperience.responsibilities.map(
                        (item, index) => (
                          <ListItem key={index} sx={{ py: 0.5 }}>
                            <Box
                              component="span"
                              sx={{ color: "#7C3AED", mr: 1 }}
                            >
                              •
                            </Box>
                            <ListItemText
                              primary={item}
                              primaryTypographyProps={{
                                sx: { color: "rgba(255,255,255,0.9)" },
                              }}
                            />
                          </ListItem>
                        )
                      )}
                    </List>

                    <Typography
                      variant="h6"
                      sx={{ color: "#7C3AED", mt: 3, mb: 2 }}
                    >
                      Technologies Used:
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        mb: 3,
                      }}
                    >
                      {selectedExperience.technologies.map((tech, index) => (
                        <Chip
                          key={index}
                          avatar={getSkillIcon(tech)}
                          label={tech}
                          sx={{
                            backgroundColor: "rgba(124, 58, 237, 0.08)",
                            color: "#7C3AED",
                            border: "1px solid rgba(124, 58, 237, 0.3)",
                            "& .MuiChip-avatar": {
                              backgroundColor: "transparent",
                            },
                          }}
                        />
                      ))}
                    </Box>

                    {selectedExperience.achievements && (
                      <>
                        <Typography
                          variant="h6"
                          sx={{ color: "#7C3AED", mb: 2 }}
                        >
                          Key Achievements:
                        </Typography>

                        <List>
                          {selectedExperience.achievements.map(
                            (item, index) => (
                              <ListItem key={index} sx={{ py: 0.5 }}>
                                <Box
                                  component="span"
                                  sx={{ color: "#7C3AED", mr: 1 }}
                                >
                                  ✓
                                </Box>
                                <ListItemText
                                  primary={item}
                                  primaryTypographyProps={{
                                    sx: { color: "rgba(255,255,255,0.9)" },
                                  }}
                                />
                              </ListItem>
                            )
                          )}
                        </List>
                      </>
                    )}
                  </>
                )}
              </Box>
            </Fade>
          </Modal>

          {/* Modal for Education Details - New Addition */}
          <Modal
            open={openEducationModal}
            onClose={handleCloseEducationModal}
            closeAfterTransition
            aria-labelledby="education-details-modal"
            aria-describedby="education-course-information"
          >
            <Fade in={openEducationModal}>
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: { xs: "90%", sm: "80%", md: "600px" },
                  bgcolor: "rgba(15, 15, 26, 0.95)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(124, 58, 237, 0.3)",
                  borderRadius: 2,
                  boxShadow: 24,
                  p: 4,
                  maxHeight: "80vh",
                  overflow: "auto",
                  "&::-webkit-scrollbar": {
                    width: "8px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "rgba(0,0,0,0.1)",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "rgba(124,58,237,0.3)",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    background: "rgba(124,58,237,0.5)",
                  },
                }}
              >
                {selectedEducation && (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="h4"
                        component="h2"
                        sx={{ color: "#7C3AED" }}
                      >
                        {selectedEducation.degree ||
                          selectedEducation.certificate}
                      </Typography>
                      <IconButton
                        onClick={handleCloseEducationModal}
                        sx={{
                          color: "rgba(255,255,255,0.7)",
                          "&:hover": { color: "#7C3AED" },
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ color: "rgba(255,255,255,0.9)" }}
                      >
                        {selectedEducation.institution}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "rgba(255,255,255,0.7)" }}
                      >
                        {selectedEducation.period}
                      </Typography>
                    </Box>

                    {/* Display minor if it exists */}
                    {selectedEducation.minor && (
                      <Typography
                        variant="subtitle1"
                        sx={{ color: "rgba(255,255,255,0.9)", mb: 3 }}
                      >
                        <Box component="span" sx={{ color: "#7C3AED", mr: 1 }}>
                          Minor:
                        </Box>
                        {selectedEducation.minor}
                      </Typography>
                    )}

                    <Typography
                      variant="body1"
                      sx={{ color: "rgba(255,255,255,0.9)", mb: 3 }}
                    >
                      {selectedEducation.description}
                    </Typography>

                    <Typography variant="h6" sx={{ color: "#7C3AED", mb: 2 }}>
                      Relevant Courses:
                    </Typography>

                    {selectedEducation.courses ? (
                      <Grid container spacing={2}>
                        {selectedEducation.courses.map((course, index) => (
                          <Grid item xs={12} sm={6} key={index}>
                            <Paper
                              elevation={0}
                              sx={{
                                p: 1.5,
                                backgroundColor: "rgba(124, 58, 237, 0.05)",
                                border: "1px solid rgba(124, 58, 237, 0.2)",
                                borderRadius: 2,
                                color: "rgba(255,255,255,0.9)",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <Box
                                component="span"
                                sx={{
                                  color: "#7C3AED",
                                  mr: 1.5,
                                  fontSize: "0.8rem",
                                }}
                              >
                                •
                              </Box>
                              {course}
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <Typography
                        variant="body1"
                        sx={{ color: "rgba(255,255,255,0.7)" }}
                      >
                        No course information available.
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            </Fade>
          </Modal>

          {/* Modal for Leadership Details */}
          <Modal
            open={openLeadershipModal}
            onClose={handleCloseLeadershipModal}
            closeAfterTransition
            aria-labelledby="leadership-details-modal"
            aria-describedby="leadership-details-information"
          >
            <Fade in={openLeadershipModal}>
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: { xs: "90%", sm: "80%", md: "600px" },
                  bgcolor: "rgba(15, 15, 26, 0.95)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(124, 58, 237, 0.3)",
                  borderRadius: 2,
                  boxShadow: 24,
                  p: 4,
                  maxHeight: "80vh",
                  overflow: "auto",
                  "&::-webkit-scrollbar": {
                    width: "8px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "rgba(0,0,0,0.1)",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "rgba(124,58,237,0.3)",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    background: "rgba(124,58,237,0.5)",
                  },
                }}
              >
                {selectedLeadership && (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="h4"
                        component="h2"
                        sx={{ color: "#7C3AED" }}
                      >
                        {selectedLeadership.title}
                      </Typography>
                      <IconButton
                        onClick={handleCloseLeadershipModal}
                        sx={{
                          color: "rgba(255,255,255,0.7)",
                          "&:hover": { color: "#7C3AED" },
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      {selectedLeadership.logo ? (
                        <Avatar
                          src={selectedLeadership.logo}
                          alt={`${selectedLeadership.organization} logo`}
                          sx={{
                            width: 60,
                            height: 60,
                            mr: 2,
                            border: "1px solid rgba(124, 58, 237, 0.3)",
                            backgroundColor: "rgba(255, 255, 255, 0.05)",
                            "& .MuiAvatar-img": {
                              objectFit: "contain",
                              padding: "6px",
                            },
                          }}
                          variant="rounded"
                        />
                      ) : (
                        <Avatar
                          sx={{
                            width: 60,
                            height: 60,
                            mr: 2,
                            backgroundColor: "rgba(124, 58, 237, 0.1)",
                            border: "1px solid rgba(124, 58, 237, 0.3)",
                          }}
                          variant="rounded"
                        >
                          <GroupsIcon
                            sx={{ color: "#7C3AED", fontSize: "2rem" }}
                          />
                        </Avatar>
                      )}
                      <Box>
                        <Typography
                          variant="h5"
                          sx={{ color: "rgba(255,255,255,0.9)" }}
                        >
                          {selectedLeadership.organization}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "rgba(255,255,255,0.7)" }}
                        >
                          {selectedLeadership.period}
                        </Typography>
                      </Box>
                    </Box>

                    <Typography
                      variant="body1"
                      sx={{ color: "rgba(255,255,255,0.9)", mb: 3 }}
                    >
                      {selectedLeadership.description}
                    </Typography>

                    <Typography
                      variant="h6"
                      sx={{ color: "#7C3AED", mb: 2 }}
                    >
                      Key Responsibilities:
                    </Typography>

                    <List>
                      {selectedLeadership.responsibilities.map(
                        (item, index) => (
                          <ListItem key={index} sx={{ py: 0.5 }}>
                            <Box
                              component="span"
                              sx={{ color: "#7C3AED", mr: 1 }}
                            >
                              •
                            </Box>
                            <ListItemText
                              primary={item}
                              primaryTypographyProps={{
                                sx: { color: "rgba(255,255,255,0.9)" },
                              }}
                            />
                          </ListItem>
                        )
                      )}
                    </List>
                  </>
                )}
              </Box>
            </Fade>
          </Modal>
        </Box>
      </Container>
    </AnimatedSection>
  );
};

export default AboutSection;
